import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Loader } from '../components/ui/Loader';
import { ProxyCard } from '../components/proxies/ProxyCard';
import { useGetProxiesQuery } from '../store/api/proxiesApi';

type StatusFilter = 'active' | 'expired' | 'cancelled' | 'all';
type ServerFilter = 1 | 2 | 3 | 4 | 'all';

export function ProxiesListPage() {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [serverFilter, setServerFilter] = useState<ServerFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [page, setPage] = useState(1);
  const limit = 20;

  const { data, isLoading, error } = useGetProxiesQuery({
    status: statusFilter !== 'all' ? statusFilter : undefined,
    serverId: serverFilter !== 'all' ? serverFilter : undefined,
    page,
    limit,
  });

  const proxies = data?.data.proxies || [];
  const pagination = data?.data.pagination;

  const countries = useMemo(() => {
    const set = new Set<string>();
    proxies.forEach((p) => { if (p.location) set.add(p.location); });
    return Array.from(set).sort();
  }, [proxies]);

  const cities = useMemo(() => {
    const set = new Set<string>();
    proxies.forEach((p) => {
      if (p.city) {
        if (!countryFilter || p.location === countryFilter) {
          set.add(p.city);
        }
      }
    });
    return Array.from(set).sort();
  }, [proxies, countryFilter]);

  const filteredProxies = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return proxies.filter((proxy) => {
      if (countryFilter && proxy.location !== countryFilter) return false;
      if (cityFilter && proxy.city !== cityFilter) return false;
      if (!q) return true;
      const fields = [
        proxy.ipAddress,
        proxy.username,
        proxy.location,
        proxy.city,
        proxy.type,
        proxy.providerName,
        proxy.status,
        String(proxy.port),
        ...(Array.isArray(proxy.allowedIps) ? proxy.allowedIps : []),
      ].filter(Boolean).map((f) => String(f).toLowerCase());
      return fields.some((f) => f.includes(q));
    });
  }, [proxies, searchQuery, countryFilter, cityFilter]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen relative">
        <div className="container mx-auto px-4 py-16 content-overlay">
          <div className="max-w-6xl mx-auto">
            <div className="flex justify-center items-center py-12">
              <Loader />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen relative">
        <div className="container mx-auto px-4 py-16 content-overlay">
          <div className="max-w-6xl mx-auto">
            <div className="card-glass rounded-2xl shadow-2xl p-8 text-center">
              <h2 className="text-2xl font-heading font-bold text-danger mb-4">
                {t('errors.network')}
              </h2>
              <p className="text-neutral-medium font-body mb-6">
                {'data' in (error as { data?: { message?: string } })
                  ? ((error as { data?: { message?: string } })?.data?.message || t('errors.network'))
                  : t('errors.network')}
              </p>
              <Link
                to="/proxies"
                className="inline-block px-6 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300"
              >
                {t('common.back')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative">
      <div className="container mx-auto px-4 py-16 content-overlay">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8 text-center animate-slide-up">
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-brand-accent mb-4">
              {t('proxies.list.title')}
            </h1>
          </div>

          {/* Filters */}
          <div className="card-glass rounded-2xl shadow-2xl p-6 mb-6 animate-scale-in">
            <div className="space-y-4">
              {/* Search Bar */}
              <div>
                <div className="relative">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-medium" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t('proxies.list.searchPlaceholder') || 'Search by IP, username, country, city...'}
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-neutral-dark bg-brand-primary/50 text-neutral-light font-body focus:outline-none focus:ring-2 focus:ring-brand-accent placeholder:text-neutral-medium/60"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-medium hover:text-neutral-light transition-colors"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>

              {/* Country & City Dropdowns */}
              {countries.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <span className="text-neutral-medium font-body mb-2 block">
                      {t('proxies.details.location')}:
                    </span>
                    <select
                      value={countryFilter}
                      onChange={(e) => {
                        setCountryFilter(e.target.value);
                        setCityFilter('');
                      }}
                      className="w-full px-4 py-2 rounded-xl border border-neutral-dark bg-brand-primary/50 text-neutral-light font-body focus:outline-none focus:ring-2 focus:ring-brand-accent"
                    >
                      <option value="">{t('common.all')}</option>
                      {countries.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <span className="text-neutral-medium font-body mb-2 block">
                      {t('proxies.details.city')}:
                    </span>
                    <select
                      value={cityFilter}
                      onChange={(e) => setCityFilter(e.target.value)}
                      disabled={!countryFilter}
                      className="w-full px-4 py-2 rounded-xl border border-neutral-dark bg-brand-primary/50 text-neutral-light font-body focus:outline-none focus:ring-2 focus:ring-brand-accent disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option value="">{t('common.all')}</option>
                      {cities.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Status Filter */}
              <div>
                <span className="text-neutral-medium font-body mb-2 block">
                  {t('proxies.list.filterStatus')}:
                </span>
                <div className="flex flex-wrap gap-2">
                  {(['all', 'active', 'expired', 'cancelled'] as StatusFilter[]).map((status) => (
                    <button
                      key={status}
                      onClick={() => {
                        setStatusFilter(status);
                        setPage(1);
                      }}
                      className={`px-4 py-2 rounded-lg font-heading font-semibold transition-all duration-300 ${
                        statusFilter === status
                          ? 'bg-brand-accent text-brand-primary'
                          : 'bg-neutral-dark text-neutral-light hover:bg-neutral-dark/80'
                      }`}
                    >
                      {status === 'all'
                        ? t('common.all')
                        : t(`proxies.status.${status}`)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Server Filter */}
              <div>
                <span className="text-neutral-medium font-body mb-2 block">
                  {t('proxies.list.filterServer')}:
                </span>
                <div className="flex flex-wrap gap-2">
                  {(['all', 1, 2, 3, 4] as ServerFilter[]).map((server) => (
                    <button
                      key={server}
                      onClick={() => {
                        setServerFilter(server);
                        setPage(1);
                      }}
                      className={`px-4 py-2 rounded-lg font-heading font-semibold transition-all duration-300 ${
                        serverFilter === server
                          ? 'bg-brand-accent text-brand-primary'
                          : 'bg-neutral-dark text-neutral-light hover:bg-neutral-dark/80'
                      }`}
                    >
                      {server === 'all'
                        ? t('common.all')
                        : t(`proxies.server.server${server}`)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Proxies List */}
          <div className="mb-6 animate-scale-in md:card-glass md:rounded-2xl md:shadow-2xl p-0 md:p-8">
            {filteredProxies.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-neutral-medium text-lg font-body mb-4">
                  {searchQuery || countryFilter || cityFilter
                    ? (t('proxies.list.noResults') || 'No proxies match your search')
                    : t('proxies.list.empty')}
                </p>
                {!(searchQuery || countryFilter || cityFilter) && (
                  <Link
                    to="/proxies/purchase"
                    className="inline-block px-6 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300"
                  >
                    {t('proxies.purchase.title')}
                  </Link>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                {filteredProxies.map((proxy) => (
                  <ProxyCard key={proxy._id} proxy={proxy} />
                ))}
              </div>
            )}
          </div>

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
                className="px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.previous')}
              </button>
              <span className="text-neutral-medium font-body px-4">
                {t('common.page')} {pagination.page} {t('common.of')} {pagination.pages}
              </span>
              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page === pagination.pages}
                className="px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.next')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
